/**
 * `<RoleSelect>` — inline role dropdown used by the members table.
 *
 * Lists the workspace's assignable roles — built-in keys (admin / member /
 * agent_editor / …) AND custom roles (B3) — fetched from
 * `/api/workspaces/:slug/roles`. Built-in options carry their key as the value;
 * custom options carry the `RoleDefinition.id`. Triggers `authClient.changeRole`
 * (the server validates the value). The mutation invalidates the members query
 * so the cell reverts cleanly on failure (FD R3/R4: LAST_ADMIN / SELF_ROLE_CHANGE).
 */

import { authClient, type AuthClientError } from "@cometchat-ai/auth/client";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from "@cometchat-ai/ui-dashboard";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { formatApiError } from "../lib/format-error";
import { listRoles, rolesKeys } from "../settings/api";

export interface RoleSelectProps {
  workspaceSlug: string;
  memberId: string;
  /** A built-in role key or a custom `RoleDefinition.id`. */
  currentRole: string;
  disabled?: boolean;
}

function humanize(key: string): string {
  return key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ");
}

export function RoleSelect({ workspaceSlug, memberId, currentRole, disabled }: RoleSelectProps) {
  const { t } = useTranslation("workspace");
  const queryClient = useQueryClient();

  const rolesQuery = useQuery({
    queryKey: rolesKeys.list(workspaceSlug),
    queryFn: () => listRoles(workspaceSlug),
    staleTime: 30_000,
  });

  const mutation = useMutation({
    mutationFn: (role: string) => authClient.changeRole(workspaceSlug, memberId, role),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["members", workspaceSlug] });
      toast.success(t("members.changeRole.savedToast"));
    },
    onError: (err: AuthClientError) => {
      void queryClient.invalidateQueries({ queryKey: ["members", workspaceSlug] });
      toast.error(t("members.changeRole.failed"), { description: formatApiError(err) });
    },
  });

  // Fall back to the two core built-ins until the list loads (avoids an empty
  // dropdown on first paint).
  const builtin = rolesQuery.data?.builtin ?? [
    { name: "member", permissions: [] },
    { name: "admin", permissions: [] },
  ];
  const custom = rolesQuery.data?.custom ?? [];

  const builtinLabel = (name: string) =>
    name === "admin"
      ? t("members.roles.admin")
      : name === "member"
        ? t("members.roles.member")
        : humanize(name);

  return (
    <Select
      value={currentRole}
      onValueChange={(value) => mutation.mutate(value)}
      disabled={disabled || mutation.isPending}
    >
      <SelectTrigger
        className="h-8 w-40 rounded-md text-[13px]"
        data-testid={`member-role-${memberId}`}
        aria-label={t("members.table.role")}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {builtin.map((r) => (
          <SelectItem
            key={r.name}
            value={r.name}
            data-testid={`member-role-option-${memberId}-${r.name}`}
          >
            {builtinLabel(r.name)}
          </SelectItem>
        ))}
        {custom.map((c) => (
          <SelectItem
            key={c.id}
            value={c.id}
            data-testid={`member-role-option-${memberId}-${c.id}`}
          >
            {/* Desk-scoped roles are authored on Helpdesk → Roles; mark them
             * so the mixed dropdown stays legible. */}
            {c.scope === "helpdesk" ? `${c.name} · Helpdesk` : c.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
